import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { NAV_LINKS } from "./data";
import { scrollToId } from "./SmoothScroll";
import { EASE } from "./motion-primitives";

const logoUrl = `${process.env.PUBLIC_URL}/holideviser-logo.svg`;

const Wordmark = ({ onClick }) => (
  <button
    onClick={onClick}
    data-testid="nav-logo"
    aria-label="Holideviser home"
    className="flex items-center transition-opacity duration-300 hover:opacity-80"
  >
    <img src={logoUrl} alt="Holideviser" className="h-7 w-auto sm:h-8" />
  </button>
);

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const go = (id) => {
    setOpen(false);
    if (id === "plan") {
      navigate("/plan-my-trip");
      return;
    }
    if (id === "discoverography") {
      navigate("/discoverography");
      return;
    }
    if (id === "destinations") {
      navigate("/destinations");
      return;
    }
    if (id === "top") {
      if (pathname !== "/") navigate("/");
      else scrollToId("top");
      return;
    }
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 450);
    } else {
      scrollToId(id);
    }
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, ease: EASE, delay: 0.1 }}
        data-testid="main-navigation"
        className={`fixed inset-x-0 top-0 z-[60] border-b transition-colors duration-500 ${
          scrolled
            ? "border-charcoal bg-ink/80 backdrop-blur-md"
            : "border-transparent bg-gradient-to-b from-ink/60 to-transparent"
        }`}
      >
        <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 sm:px-10 lg:px-16">
          <Wordmark onClick={() => go("top")} />

          <div className="hidden items-center gap-10 lg:flex">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                data-testid={`nav-link-${l.id}`}
                className="link-underline font-sans text-sm tracking-[0.02em] text-cream/90 hover:text-cream"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => go("plan")}
              data-testid="nav-plan-trip-cta"
              className="rounded-none bg-teal px-6 py-3 font-sans text-xs uppercase tracking-[0.18em] text-ink transition-colors duration-500 hover:bg-cream"
            >
              Plan My Trip
            </button>
          </div>

          <button
            onClick={() => setOpen(true)}
            data-testid="nav-hamburger-open"
            aria-label="Open menu"
            className="text-cream lg:hidden"
          >
            <Menu size={26} strokeWidth={1.4} />
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="mobile-menu"
            className="fixed inset-0 z-[70] bg-ink lg:hidden"
          >
            <div className="flex items-center justify-between px-6 py-5 sm:px-10">
              <Wordmark onClick={() => go("top")} />
              <button
                onClick={() => setOpen(false)}
                data-testid="nav-hamburger-close"
                aria-label="Close menu"
                className="text-cream"
              >
                <X size={26} strokeWidth={1.4} />
              </button>
            </div>
            <div className="flex flex-col gap-2 px-6 pt-10 sm:px-10">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i + 0.1, ease: EASE, duration: 0.6 }}
                  onClick={() => go(l.id)}
                  data-testid={`mobile-nav-link-${l.id}`}
                  className="border-b border-charcoal py-5 text-left font-serif text-3xl font-light text-cream"
                >
                  {l.label}
                </motion.button>
              ))}
              <button
                onClick={() => go("plan")}
                data-testid="mobile-plan-trip-cta"
                className="mt-10 rounded-none bg-teal px-6 py-4 text-center font-sans text-sm uppercase tracking-[0.18em] text-ink"
              >
                Plan My Trip
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
